import {
  contentByKey,
  contentKeyByApplianceCode,
  defaultRepairEstimatesUrl,
  genericOnlyBrands,
  PersonalisedQuoteContentKey,
  repairEstimatesUrlByBrand,
} from './personalisedQuoteContent';

export type PersonalisedQuoteContentInput = {
  applianceCode?: string;
  applianceName?: string;
  brandCode?: string;
};

export type PersonalisedQuoteContent = {
  contentKey: PersonalisedQuoteContentKey;
  applianceType: string;
  repairCost?: number;
  repairBody: string;
  repairEstimatesUrl?: string;
};

/**
 * Picks the personalised quote content for the selected appliance.
 *
 * @param input.applianceCode Catalogue appliance code from the form (e.g. `WSHR`); unknown or missing → generic.
 *   Pages limited to some brands (washer/dryer: Whirlpool, Maytag and Amana; stand mixer: KitchenAid) fall back to generic elsewhere.
 *   JennAir always gets the generic content.
 * @param input.applianceName Catalogue appliance name, shown lower-cased as the appliance type.
 * @param input.brandCode Tenant brand code (`VITE_BRAND_CODE`), used for brand-limited pages and the repair estimates link.
 */
const getContentKey = (
  applianceCode: string | undefined,
  brandCode: string | undefined,
): PersonalisedQuoteContentKey => {
  const contentKey = contentKeyByApplianceCode[applianceCode?.trim().toUpperCase() ?? ''];
  const brands = contentKey && contentByKey[contentKey].brands;

  if (
    !contentKey ||
    (brandCode && genericOnlyBrands.includes(brandCode)) ||
    (brands && !(brandCode && brands.includes(brandCode)))
  ) {
    return 'generic';
  }

  return contentKey;
};

export const getPersonalisedQuoteContent = ({
  applianceCode,
  applianceName,
  brandCode,
}: PersonalisedQuoteContentInput): PersonalisedQuoteContent => {
  const contentKey = getContentKey(applianceCode, brandCode);
  const { repairCost, repairBody, showRepairEstimates } = contentByKey[contentKey];

  return {
    contentKey,
    applianceType: applianceName?.trim().toLowerCase() || 'appliance',
    repairCost,
    repairBody,
    repairEstimatesUrl: showRepairEstimates
      ? ((brandCode ? repairEstimatesUrlByBrand[brandCode] : undefined) ??
        defaultRepairEstimatesUrl)
      : undefined,
  };
};
